import React, { type FC } from 'react';
import { defineMessages } from 'react-intl';

import AiAgentIcon from '@atlaskit/icon/core/ai-agent';

import { LoadableLazy } from '@confluence/loadable';
import type { ObjectSidebarPanel } from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';

import { AGENT_TRUST_RECEIPT_PANEL_ID, type AgentTrustReceipt } from './agentTrustReceiptModel.ts';

const i18n = defineMessages({
	headerLabel: {
		id: 'agent-trust-receipt.panel.header.non-final',
		defaultMessage: 'Agent receipt',
		description: 'Object sidebar panel header for the Agent Trust Receipt feature.',
	},
});

type CreateAgentTrustReceiptPanelOptions = {
	receipt: AgentTrustReceipt;
};

type AgentTrustReceiptBodyProps = {
	receipt: AgentTrustReceipt;
};

const AgentReceiptHeaderIcon: FC = () => <AiAgentIcon label="" color="currentColor" />;

// Interaction-only, gated panel body — lazy so its chunk only loads when the panel is opened, and
// never ships in the byline/right-rail entry-point bundles that statically import this factory.
const LazyAgentTrustReceiptPanelBody = LoadableLazy<AgentTrustReceiptBodyProps>({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AgentTrustReceiptPanelBody" */ './AgentTrustReceiptPanelBody.tsx'
			)
		).AgentTrustReceiptPanelBody,
});

const AgentTrustReceiptBodyComponent: FC<AgentTrustReceiptBodyProps> = ({ receipt }) => (
	<LazyAgentTrustReceiptPanelBody receipt={receipt} />
);

export const createAgentTrustReceiptPanel = ({
	receipt,
}: CreateAgentTrustReceiptPanelOptions): ObjectSidebarPanel => ({
	id: AGENT_TRUST_RECEIPT_PANEL_ID,
	headerComponentElements: {
		HeaderIcon: AgentReceiptHeaderIcon,
		headerLabel: i18n.headerLabel,
	},
	BodyComponent: AgentTrustReceiptBodyComponent as ObjectSidebarPanel['BodyComponent'],
	props: { receipt } as ObjectSidebarPanel['props'],
});
